import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, ElementRef,
    EventEmitter,
    OnDestroy,
    Output,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {ConfirmComponent} from "../../../../../../../common/components/confirm/confirm.component";
import {Base} from "../../../../../../../common/interfaces/base.interfaces";
import {Api, ApiService} from "../../../../../../../common/services/api.service";
import {DragulaService} from "ng2-dragula";
import {ToastService} from "../../../../../../../common/services/toast.service";
import {Modal, ModalService} from "../../../../../../services/modal.service";
import {Warehouse} from "../../../../../../../common/interfaces/warehouse.interface";
import {takeUntil} from "rxjs/operators";
import {SpinnerService} from "../../../../../../../common/services/spinner.service";
import {HelpersService} from "../../../../../../../common/services/helpers.service";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {ShipmentFormComponent} from "../form/form.component";
import {Api3Service} from "../../../../../../../common/services/api3.service";
import autoScroll from "dom-autoscroller";


@Component({
    selector: "section-order-shipment-wizard-step1",
    templateUrl: "step1.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class OrderShipmentWizardStep1Component implements OnDestroy, AfterViewInit {

    private state: Base.IState;

    private destroy$: EventEmitter<boolean> = new EventEmitter(false);

    private scrollInstance: any;

    @Output()
    public result: EventEmitter<any> = new EventEmitter();

    @ViewChild("scrollContainer")
    public scrollContainer: ElementRef;

    @ViewChild(ConfirmComponent, {static: false})
    public confirmRef: ConfirmComponent;

    public bagName: string;

    /**
     * Order items
     */
    public items: any[] = [];

    public shipments: Warehouse.IShipment[] = [];

    public selectedList: number[] = [];

    public allFreeItemsSelected: boolean = false;

    public allShipmentItemsSelected: boolean[] = [];

    public constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private apiService: ApiService,
        private api3Service: Api3Service,
        private toastService: ToastService,
        private dragulaService: DragulaService,
        private modalService: ModalService,
        private spinnerService: SpinnerService
    ) {

        this.bagName = "shipment-bag_" + HelpersService.randomString();

        if (!dragulaService.find(this.bagName)) {
            dragulaService.createGroup(this.bagName, {});
        }
        dragulaService.dropModel(this.bagName).pipe(takeUntil(this.destroy$))
            .subscribe((value: any): void => {
                this.onDropModel(value);
            });
    }

    /**
     * Handle drop event
     * @returns {Promise<any>}
     */
    private async onDropModel(value: any): Promise<any> {

        const item: any = value.el;
        const target: any = value.target;
        const bag_name: any = value.name;

        if (bag_name !== this.bagName) {
            return;
        }
        let itemId: string;
        item.classList.forEach((className: string): void => {
            if (/id-[0-9]+/.test(className)) {
                itemId = className.replace("id-", "");
            }
        });

        let targetId: number = null;
        target.classList.forEach((className: string): void => {
            if (/id-[0-9]+/.test(className)) {
                targetId = Number(className.replace("id-", ""));
            }
        });

        if (itemId && this.selectedList.indexOf(Number(itemId)) === -1) {
            this.selectedList = [Number(itemId)];
        }

        if (this.selectedList.length) {
            this.spinnerService.show();

            if (targetId) {
                const linkRes: Api.IResponse = await this.api3Service.post(
                    `${this.state.section}/shipments/${targetId}/link-items`,
                    {order_items: this.selectedList});

                this.toastService.show(linkRes.message, linkRes.type as string);
            } else {
                for (const id of this.selectedList) {
                    const unlinkRes: Api.IResponse = await this.api3Service.post(
                        `${this.state.section}/order-items/${id}/unlink-from-shipment`);

                    this.toastService.show(unlinkRes.message, unlinkRes.type as string);
                }
            }

            this.spinnerService.hide();

            this.getFreeOrderItems();
            this.getOrderShipments();
            this.result.next(this.shipments);

            this.selectedList = [];
            this.checkAllSelected();
        }

        this.changeDetectorRef.markForCheck();
    }


    private async getOrderShipments(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.get(
            `${this.state.section}/orders/${this.state.params.order_id}/shipments`,
            {
                type: this.state.params.type,
                relations: [
                    "OrderItems"
                ]
            });
        this.spinnerService.hide();
        if (data) {
            this.shipments = data;

            for (const s of this.shipments) {
                this.allShipmentItemsSelected.push(false);
            }

            if (this.shipments.length > 0) {
                this.result.next(this.shipments);
            }
        }
    }

    /**
     * Get free order items
     * @returns {Promise<any>}
     */
    private async getFreeOrderItems(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse =
            await this.apiService.request(Api.EMethod.Get,
                ["order", this.state.params.order_id, "items", this.state.params.type],
                null, {
                    linked_to_shipment: "false"
                });
        this.spinnerService.hide();
        if (data) {
            this.items = data;
            this.changeDetectorRef.markForCheck();
        }
    }

    private checkAllSelected(): void {
        this.allFreeItemsSelected = false;
        this.allShipmentItemsSelected = [];

        this.allFreeItemsSelected = this.items.length
            && this.items.every((it: any) => this.selectedList.indexOf(it.id) > -1);

        for (const index in this.shipments) {
            this.allShipmentItemsSelected[index] = this.shipments[index].order_items.length
                && this.shipments[index].order_items.every((it: any) => this.selectedList.indexOf(it.id) > -1);
        }

        this.changeDetectorRef.markForCheck();
    }

    /**
     * Initialize details step
     * @param state
     * @returns {Promise<any>}
     */
    public async init(state: Base.IState): Promise<any> {
        this.state = state;

        this.getFreeOrderItems();
        this.getOrderShipments();

        this.changeDetectorRef.markForCheck();

    }

    public async add(): Promise<any> {
        const response: Modal.IResponse = await this.modalService.open(ShipmentFormComponent, {
            action: "add",
            order_id: this.state.params.order_id,
            type: this.state.params.type,
            modalWidth: "1000px"
        });
        if (response) {
            this.getOrderShipments();
        }
    }

    public async edit(shipment: Warehouse.IShipment): Promise<any> {

        const response: Modal.IResponse = await this.modalService.open(ShipmentFormComponent, {
            action: "edit",
            shipment: shipment,
            order_id: this.state.params.order_id,
            modalWidth: "1000px",
            type: this.state.params.type,
        });
        if (response) {
            this.getOrderShipments();
        }
    }

    public selectedListUpdate(event: MatCheckboxChange): void {

        const itemId: number = Number(event.source.value);
        if (event.checked) {
            if (this.selectedList.indexOf(itemId) === -1) {
                this.selectedList.push(itemId);
            }
        } else {
            const index: number = this.selectedList.indexOf(itemId);
            if (index > -1) {
                this.selectedList.splice(index, 1);
            }
        }

        this.checkAllSelected();
    }

    public selectAll(event: MatCheckboxChange, itemsToSelect: any[]): void {
        this.selectedList = [];

        for (const shipment of this.shipments) {
            this.allShipmentItemsSelected.push(false);
        }

        if (event.checked) {
            for (const item of itemsToSelect) {
                this.selectedList.push(item.id);
            }
        }
        this.checkAllSelected();
        this.changeDetectorRef.markForCheck();
    }


    public ngAfterViewInit(): void {
        this.scrollInstance = autoScroll(
            [this.scrollContainer.nativeElement],
            {
                margin: 20,
                maxSpeed: 5,
                scrollWhenOutside: true,
                autoScroll: () => {
                    return this.dragulaService.find(this.bagName).drake.dragging;
                }
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();

        if (this.scrollInstance) {
            this.scrollInstance.destroy();
        }
    }

}
